import { useEffect, useState } from "react";
import { FilterList, useListContext } from "react-admin";
import moment from "moment";
import { Button, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SortIcon from "@mui/icons-material/Sort";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { colors } from "../../data/tokens";

// Rótulos dos campos: peso 600, cor padrão de rótulo, colados no campo
const labelSx = {
  fontSize: 12.5,
  fontWeight: 600,
  color: colors.textMuted,
  marginBottom: 0.25,
  lineHeight: 1.3,
};

// Array (não Fragment): o Select do MUI exige MenuItems diretos
const dateFieldChoices = [
  { value: "createdAt", label: "Criação da conta" },
  { value: "updatedAt", label: "Atualização da conta" },
  { value: "effectiveDate", label: "Efetivo" },
  { value: "admissionDate", label: "Admissão" },
  { value: "licenseDate", label: "Licenciado" },
  { value: "collectiveDate", label: "Coletivo" },
  { value: "remissionDate", label: "Remido" },
  { value: "aspirantDate", label: "Aspirante" },
  { value: "cancellationDate", label: "Cancelado" },
  { value: "contributionDate", label: "Contribuinte" },
  { value: "collectiveRemissionDate", label: "Coletivo Remido" },
  { value: "birthDate", label: "Nascimento" },
  { value: "deceasedDate", label: "Falecimento" },
  { value: "seniorDate", label: "Sênior" },
];

export const PostFilterSidebar = () => {
  const {
    displayedFilters,
    filterValues,
    setFilters,
    sort,
    setSort,
    hideFilter,
  } = useListContext();

  const userRole = localStorage.getItem("roles");

  // Estado local inicia do filtro já aplicado (sobrevive a recarregar a
  // página com o filtro na URL — antes o painel abria vazio mesmo com a
  // lista filtrada)
  const [dateField, setDateField] = useState(filterValues?.dateField ?? "");
  const [dateFieldValue, setDateFieldValue] = useState<any>(
    filterValues?.dateFieldValue ?? ""
  );
  const [dateFilterType, setDateFilterType] = useState(
    filterValues?.dateFilterType ?? ""
  );
  const [sortDateField, setSortDateField] = useState("createdAt");
  const [sortDateOrder, setSortDateOrder] = useState("DESC");

  // "Limpar filtros" (toolbar) zera o estado da lista, mas os campos
  // deste painel são estado local: sem esta sincronização eles seguiam
  // exibindo a seleção antiga — parecia que a data não tinha sido limpa
  // e um clique em Filtrar reaplicava o filtro "fantasma". A limpeza
  // global é o único caminho que esvazia o estado por inteiro, então a
  // condição não atropela uma seleção em andamento (ex.: digitar na
  // busca antes de clicar em Filtrar).
  useEffect(() => {
    if (!filterValues || Object.keys(filterValues).length === 0) {
      setDateField("");
      setDateFilterType("");
      setDateFieldValue("");
    }
  }, [filterValues]);

  // Espelha a ordenação aplicada (o "Limpar filtros" também volta a
  // ordenação padrão); ordenar por coluna fora das opções de data não
  // mexe nos seletores
  useEffect(() => {
    if (sort && dateFieldChoices.some((o) => o.value === sort.field)) {
      setSortDateField(sort.field);
      setSortDateOrder(sort.order);
    }
  }, [sort]);

  const submitSort = (e: any) => {
    setSort({ field: sortDateField, order: sortDateOrder });
  };
  const submitFilter = (e: any) => {
    localStorage.setItem("dateField", dateField);
    const newFilterValues = { ...filterValues };
    newFilterValues["dateField"] = dateField;
    newFilterValues["dateFilterType"] = dateFilterType;
    newFilterValues["dateFieldValue"] = dateFieldValue;
    setFilters(newFilterValues, {});
  };

  const form = useForm({
    defaultValues: filterValues,
  });
  const onSubmit = (values: any) => {
    if (Object.keys(values).length > 0) {
      setFilters(values, {});
    } else {
      hideFilter("main");
    }
  };

  return (
    // Painel compacto: campos pequenos, botões de tamanho normal e uma
    // divisória separando do restante da lista (fica dentro do cartão)
    <Box
      sx={{
        borderBottom: `1px solid ${colors.border}`,
        // (a compensação de -20px saiu: o mt embutido do título do
        // FilterList agora é zerado na origem)
        marginTop: 0.5,
        marginBottom: 1,
        paddingBottom: 1.5,
        // Compacta as listas internas do FilterList (títulos das seções)
        "& .MuiList-root": { paddingTop: 0, paddingBottom: 0 },
        "& .MuiListItem-root": { paddingTop: 0, paddingBottom: 0.5 },
        // Título da seção (Typography variant="overline" do FilterList):
        // cor primária e peso, sem vazar para rótulos/botões
        "& .MuiTypography-overline": {
          fontWeight: 700,
          color: colors.primary,
        },
        // O Box do título (1º filho do Box raiz do FilterList) traz
        // mt:2 (16px) embutido — zera; ícone da seção na cor primária
        "& .MuiGrid-item > .MuiBox-root > .MuiBox-root:first-of-type": {
          marginTop: 0,
          "& svg": { color: colors.primary },
        },
        // Respiro de 3px entre o rótulo externo e o campo (DevTools)
        "& .MuiFormControl-root": { marginTop: "3px" },
        // Altura única e determinística (48px) para TODOS os campos do
        // painel — sobrepõe as tabelas internas de padding do MUI, que
        // variam entre select, texto e date picker
        "& .MuiFilledInput-root": { height: 48 },
        "& .MuiFilledInput-input": {
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Grid container columnSpacing={2} rowSpacing={1}>
        <Grid item xs={12} md={6}>
          <FilterList label="FILTRO DE DATAS" icon={<FilterAltIcon />}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <Typography sx={labelSx}>Campo de data</Typography>
                <TextField
                  select
                  variant="filled"
                  hiddenLabel
                  value={dateField}
                  onChange={(e) => setDateField(e.target.value as string)}
                  fullWidth
                  SelectProps={{
                    // placeholder enquanto nada foi selecionado
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        dateFieldChoices.find((o) => o.value === selected)
                          ?.label ?? selected
                      ) : (
                        <span style={{ color: colors.textMuted }}>
                          Selecione o campo…
                        </span>
                      ),
                  }}
                >
                  {dateFieldChoices.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {dateField && (
                <Box sx={{ minWidth: 180, flex: 1 }}>
                  <Typography sx={labelSx}>Tipo de filtragem</Typography>
                  <TextField
                    select
                    variant="filled"
                    hiddenLabel
                    value={dateFilterType}
                    onChange={(e) =>
                      setDateFilterType(e.target.value as string)
                    }
                    fullWidth
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected: any) => {
                        const labels: Record<string, string> = {
                          lte: "Até uma data",
                          gte: "A partir de uma data",
                          range: "Entre duas datas",
                        };
                        return selected ? (
                          labels[selected] ?? selected
                        ) : (
                          <span style={{ color: colors.textMuted }}>
                            Selecione o tipo…
                          </span>
                        );
                      },
                    }}
                  >
                    <MenuItem value={"lte"}>Até uma data</MenuItem>
                    <MenuItem value={"gte"}>A partir de uma data</MenuItem>
                    <MenuItem value={"range"}>Entre duas datas</MenuItem>
                  </TextField>
                </Box>
              )}
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="pt-br"
              >
                {/* Pickers CONTROLADOS (value vindo do estado): sem isso
                    a limpeza zera o estado mas o texto da data ficava na
                    tela. Guarda de nulo no onChange: apagar o texto ou
                    digitar data inválida não pode derrubar a tela. */}
                {dateFilterType === "lte" && (
                  <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Typography sx={labelSx}>Data até</Typography>
                    <DatePicker
                      slotProps={{
                        textField: {
                          variant: "filled",
                          hiddenLabel: true,
                          fullWidth: true,
                        },
                      }}
                      value={
                        dateFieldValue?.dateEnd
                          ? moment(dateFieldValue.dateEnd)
                          : null
                      }
                      onChange={(v: any) => {
                        setDateFieldValue({
                          dateStart: null,
                          dateEnd: v?.isValid()
                            ? v.format("YYYY-MM-DD")
                            : null,
                        });
                      }}
                    />
                  </Box>
                )}
                {dateFilterType === "gte" && (
                  <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Typography sx={labelSx}>A partir de</Typography>
                    <DatePicker
                      slotProps={{
                        textField: {
                          variant: "filled",
                          hiddenLabel: true,
                          fullWidth: true,
                        },
                      }}
                      value={
                        dateFieldValue?.dateStart
                          ? moment(dateFieldValue.dateStart)
                          : null
                      }
                      onChange={(v: any) =>
                        setDateFieldValue({
                          dateStart: v?.isValid()
                            ? v.format("YYYY-MM-DD")
                            : null,
                          dateEnd: null,
                        })
                      }
                    />
                  </Box>
                )}
                {dateFilterType === "range" && (
                  <>
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                      <Typography sx={labelSx}>De</Typography>
                      <DatePicker
                        slotProps={{
                          textField: {
                            variant: "filled",
                            hiddenLabel: true,
                            fullWidth: true,
                          },
                        }}
                        value={
                          dateFieldValue?.dateStart
                            ? moment(dateFieldValue.dateStart)
                            : null
                        }
                        onChange={(v: any) =>
                          setDateFieldValue({
                            dateStart: v?.isValid()
                              ? v.format("YYYY-MM-DD")
                              : null,
                            dateEnd: dateFieldValue?.dateEnd ?? null,
                          })
                        }
                      />
                    </Box>
                    <Box sx={{ minWidth: 180, flex: 1 }}>
                      <Typography sx={labelSx}>Até</Typography>
                      <DatePicker
                        slotProps={{
                          textField: {
                            variant: "filled",
                            hiddenLabel: true,
                            fullWidth: true,
                          },
                        }}
                        value={
                          dateFieldValue?.dateEnd
                            ? moment(dateFieldValue.dateEnd)
                            : null
                        }
                        onChange={(v: any) =>
                          setDateFieldValue({
                            dateStart: dateFieldValue?.dateStart ?? null,
                            dateEnd: v?.isValid()
                              ? v.format("YYYY-MM-DD")
                              : null,
                          })
                        }
                      />
                    </Box>
                  </>
                )}
              </LocalizationProvider>
              {/* Wrapper esticado com o botão pinado no fundo da linha:
                  base do botão = base dos campos, por construção */}
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "flex-end",
                  // compensa os 4px de folga da linha (validado no DevTools)
                  marginBottom: 0.5,
                }}
              >
                <Button
                  onClick={submitFilter}
                  variant="contained"
                  sx={{ height: 48 }}
                  startIcon={<ManageSearchIcon />}
                >
                  Filtrar
                </Button>
              </Box>
              {/* O botão de limpar filtros agora é global, na toolbar
                  do topo da lista (ClearAllFiltersButton) */}
            </Box>
          </FilterList>
        </Grid>
        <Grid item xs={12} md={6}>
          <FilterList label="ORDENAR POR DATAS" icon={<SortIcon />}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <Typography sx={labelSx}>Campo para ordenar</Typography>
                <TextField
                  select
                  variant="filled"
                  hiddenLabel
                  value={sortDateField}
                  onChange={(e) => setSortDateField(e.target.value as string)}
                  fullWidth
                >
                  {dateFieldChoices.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ minWidth: 150, flex: 1 }}>
                <Typography sx={labelSx}>Ordem</Typography>
                <TextField
                  select
                  variant="filled"
                  hiddenLabel
                  value={sortDateOrder}
                  onChange={(e) => setSortDateOrder(e.target.value as string)}
                  fullWidth
                >
                  <MenuItem value={"DESC"}>Descendente</MenuItem>
                  <MenuItem value={"ASC"}>Ascendente</MenuItem>
                </TextField>
              </Box>
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "flex-end",
                  // compensa os 4px de folga da linha (validado no DevTools)
                  marginBottom: 0.5,
                }}
              >
                <Button
                  onClick={submitSort}
                  variant="contained"
                  sx={{ height: 48 }}
                  startIcon={<SortIcon />}
                >
                  Ordenar
                </Button>
              </Box>
            </Box>
          </FilterList>
        </Grid>
      </Grid>
    </Box>
  );
};
